import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PriorityBadge } from "../components/crm/Badges";
import { fmtDateTime, ACTIVITY_COLORS, ACTIVITY_ICONS } from "../lib/utils";
import * as PhosphorIcons from "@phosphor-icons/react";
import { CheckCircle, Circle, ArrowSquareOut, MagnifyingGlass, FunnelSimple, Trash, Plus, DownloadSimple, CaretUp, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import AddActivityModal from "../components/crm/AddActivityModal";
import BulkActionBar from "../components/crm/BulkActionBar";
import ConfirmDialog from "../components/crm/ConfirmDialog";
import { API_BASE } from "../lib/api";
import {
  loadPrefs, savePrefs, clearPrefs,
  loadSession, saveSession, clearSession,
} from "../lib/tableMemory";

const ACTIVITY_TYPE_OPTIONS = ["CALL", "EMAIL", "WHATSAPP", "SAMPLE", "ORDER", "MEETING", "NOTE"];

// UI-STABILITY-1: defaults + allow-lists for safe restoration.
const DEFAULT_ACT_FILTERS = {
  status: "All", type: "All", date: "All", priority: "All",
  agent_id: "All", search: "",
};
const DEFAULT_ACT_SORT = { key: null, dir: "asc" };
const VALID_ACT_STATUS = new Set(["All", "Open", "Completed"]);
const VALID_ACT_TYPE = new Set(["All", ...ACTIVITY_TYPE_OPTIONS]);
const VALID_ACT_DATE = new Set(["All", "today", "overdue", "tomorrow", "next7", "next30"]);
const VALID_ACT_PRIORITY = new Set(["All", "A", "B", "C"]);
const VALID_ACT_SORT_KEYS = new Set([
  null, "activity_type", "title", "lead_company", "agent_name",
  "activity_date", "next_follow_up_date", "updated_at", "status", "priority",
]);

function sanitizeActFilters(saved, user) {
  if (!saved || typeof saved !== "object") return null;
  const f = { ...DEFAULT_ACT_FILTERS };
  f.status = VALID_ACT_STATUS.has(saved.status) ? saved.status : "All";
  f.type = VALID_ACT_TYPE.has(saved.type) ? saved.type : "All";
  f.date = VALID_ACT_DATE.has(saved.date) ? saved.date : "All";
  f.priority = VALID_ACT_PRIORITY.has(saved.priority) ? saved.priority : "All";
  // Admin-only filter — drop for agents.
  f.agent_id = user?.role === "admin"
    ? (typeof saved.agent_id === "string" ? saved.agent_id : "All")
    : "All";
  return f;
}

function sanitizeActSort(saved) {
  if (!saved || typeof saved !== "object") return null;
  const key = saved.key === null || VALID_ACT_SORT_KEYS.has(saved.key) ? saved.key : null;
  const dir = saved.dir === "asc" ? "asc" : "desc";
  return { key, dir };
}

function activityTitle(act, lead) {
  const company = lead?.company_name || "Lead";
  const map = {
    CALL: `Call with ${company}`,
    EMAIL: `Email with ${company}`,
    WHATSAPP: `WhatsApp with ${company}`,
    SAMPLE: `Sample sent to ${company}`,
    ORDER: `Order with ${company}`,
    MEETING: `Meeting with ${company}`,
    NOTE: `Note with ${company}`,
  };
  return map[act.activity_type] || act.title || `${act.activity_type} with ${company}`;
}

function todayISO() {
  const d = new Date(); d.setHours(0, 0, 0, 0);
  return d.toISOString();
}
function endOfDayISO() {
  const d = new Date(); d.setHours(23, 59, 59, 999);
  return d.toISOString();
}
function tomorrowRangeISO() {
  const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + 1);
  const e = new Date(d); e.setDate(e.getDate() + 1);
  return [d.toISOString(), e.toISOString()];
}
function nextNDaysISO(n) {
  const d = new Date(); d.setHours(0, 0, 0, 0);
  const e = new Date(d); e.setDate(e.getDate() + n);
  return [d.toISOString(), e.toISOString()];
}

export default function Activities() {
  const { user } = useAuth();
  const [activities, setActivities] = useState([]);
  const [leads, setLeads] = useState([]);
  const [agents, setAgents] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_ACT_FILTERS);
  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [sort, setSort] = useState(DEFAULT_ACT_SORT);
  const [restored, setRestored] = useState(false);
  const isAdmin = user?.role === "admin";

  // UI-STABILITY-1: restore persisted state once user is known.
  useEffect(() => {
    if (!user || restored) return;
    const savedPrefs = loadPrefs(user, "activities");
    const cleanedFilters = sanitizeActFilters(savedPrefs?.filters, user);
    const cleanedSort = sanitizeActSort(savedPrefs?.sort);
    const savedSession = loadSession(user, "activities");
    const savedSearch =
      savedSession && typeof savedSession.search === "string"
        ? savedSession.search
        : "";
    setFilters((prev) => ({
      ...(cleanedFilters || prev),
      search: savedSearch,
    }));
    if (cleanedSort) setSort(cleanedSort);
    setRestored(true);
  }, [user, restored]);

  useEffect(() => {
    if (!user || !restored) return;
    const { search: _ignored, ...persist } = filters;
    void _ignored;
    savePrefs(user, "activities", { filters: persist, sort });
  }, [user, restored, filters, sort]);

  useEffect(() => {
    if (!user || !restored) return;
    saveSession(user, "activities", { search: filters.search || "" });
  }, [user, restored, filters.search]);

  const resetFilters = () => {
    setFilters(DEFAULT_ACT_FILTERS);
    setSort(DEFAULT_ACT_SORT);
    clearPrefs(user, "activities");
    clearSession(user, "activities");
  };

  const leadsMap = useMemo(() => Object.fromEntries(leads.map((l) => [l.id, l])), [leads]);
  const agentsMap = useMemo(() => Object.fromEntries(agents.map((a) => [a.id, a])), [agents]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status !== "All") params.status = filters.status;
      if (filters.type !== "All") params.activity_type = filters.type;
      if (filters.priority !== "All") params.priority = filters.priority;
      if (filters.agent_id !== "All" && user?.role === "admin") params.agent_id = filters.agent_id;
      if (filters.search.trim()) params.search = filters.search.trim();
      // Date filter
      if (filters.date === "today") {
        params.date_from = todayISO(); params.date_to = endOfDayISO();
      } else if (filters.date === "overdue") {
        params.date_to = new Date().toISOString();
        params.status = "Open";
      } else if (filters.date === "tomorrow") {
        const [a, b] = tomorrowRangeISO(); params.date_from = a; params.date_to = b;
      } else if (filters.date === "next7") {
        const [a, b] = nextNDaysISO(7); params.date_from = a; params.date_to = b;
      } else if (filters.date === "next30") {
        const [a, b] = nextNDaysISO(30); params.date_from = a; params.date_to = b;
      }
      const [a, l, u] = await Promise.all([
        api.get("/activities", { params }),
        api.get("/leads"),
        api.get("/users"),
      ]);
      setActivities(a.data); setLeads(l.data); setAgents(u.data);
    } catch (e) {
      toast.error("Failed to load activities");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // UI-STABILITY-1B: defer the first fetch until persisted prefs are
    // restored to avoid an unfiltered table flash.
    if (!restored) return;
    setSelected(new Set());
    load();
    /* eslint-disable-next-line */
  }, [filters, restored]);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allSelected = activities.length > 0 && activities.every((a) => selected.has(a.id));
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(activities.map((a) => a.id)));
  };

  // UI-STABILITY-1B-tier1: memoize agent dropdown source.
  const agentOptions = useMemo(
    () => agents.filter((a) => a.role === "agent"),
    [agents]
  );

  const bulkComplete = async () => {
    try {
      const { data } = await api.post("/activities/bulk/complete", { activity_ids: Array.from(selected) });
      toast.success(`Completed ${data.updated} activities`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const bulkDelete = async () => {
    try {
      const { data } = await api.post("/activities/bulk/delete", { activity_ids: Array.from(selected) });
      toast.success(`Deleted ${data.deleted} activities`);
      setSelected(new Set());
      setConfirmDelete(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const toggleStatus = async (act) => {
    const newStatus = act.status === "Completed" ? "Open" : "Completed";
    try {
      await api.put(`/activities/${act.id}`, { status: newStatus });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const deleteAct = async (act) => {
    if (!window.confirm("Delete this activity?")) return;
    try {
      await api.delete(`/activities/${act.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const openEdit = (act) => { setEditing(act); setCreateMode(false); setModalOpen(true); };
  const openCreate = () => { setEditing(null); setCreateMode(true); setModalOpen(true); };

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (filters.status !== "All") params.set("status", filters.status);
    if (filters.type !== "All") params.set("activity_type", filters.type);
    if (filters.priority !== "All") params.set("priority", filters.priority);
    if (filters.agent_id !== "All" && isAdmin) params.set("agent_id", filters.agent_id);
    if (filters.search.trim()) params.set("search", filters.search.trim());
    const qs = params.toString();
    window.open(`${API_BASE}/activities/csv/export${qs ? `?${qs}` : ""}`, "_blank");
  };

  const exportSelected = () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected).join(",");
    window.open(`${API_BASE}/activities/csv/export?activity_ids=${encodeURIComponent(ids)}`, "_blank");
  };

  const onSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return { key: null, dir: "asc" };
    });
  };

  const sortedActivities = useMemo(() => {
    if (!sort.key) return activities;
    const accessor = (a) => {
      switch (sort.key) {
        case "activity_type": return a.activity_type || "";
        case "title": return activityTitle(a, leadsMap[a.lead_id]) || "";
        case "lead_company": return leadsMap[a.lead_id]?.company_name || "";
        case "agent_name": return agentsMap[a.agent_id]?.name || "";
        case "activity_date": return a.activity_date || "";
        case "next_follow_up_date": return a.next_follow_up_date || "";
        case "updated_at": return a.updated_at || a.created_at || "";
        case "status": return a.status || "";
        case "priority": return a.priority || "";
        default: return "";
      }
    };
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...activities].sort((a, b) => {
      const va = accessor(a), vb = accessor(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [activities, sort, leadsMap, agentsMap]);

  const SortHeader = ({ label, k }) => (
    <button onClick={() => onSort(k)} data-testid={`sort-activities-${k}`}
      className="inline-flex items-center gap-1 hover:text-[#1A3A21]">
      {label}
      {sort.key === k && (
        sort.dir === "asc"
          ? <CaretUp size={11} weight="bold" />
          : <CaretDown size={11} weight="bold" />
      )}
    </button>
  );

  const isOverdue = (act) => {
    if (act.status !== "Open" || !act.next_follow_up_date) return false;
    return new Date(act.next_follow_up_date) < new Date();
  };

  return (
    <div className="space-y-6" data-testid="activities-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#E1A104] font-semibold">Activities</div>
          <h1 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight mt-1">
            {user?.role === "admin" ? "All Activities" : "My Activities"}
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowFilters((s) => !s)} data-testid="activities-toggle-filters"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50">
            <FunnelSimple size={16} /> Filters
          </button>
          <button onClick={resetFilters} data-testid="activities-reset-filters"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50 text-gray-600">
            Reset filters
          </button>
          {isAdmin && (
            <button onClick={exportCsv} data-testid="activities-export-csv"
              className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50">
              <DownloadSimple size={16} /> Export CSV
            </button>
          )}
          <button onClick={openCreate} data-testid="add-activity-button"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-md bg-[#E1A104] text-[#1A3A21] hover:bg-[#C98F03]">
            <Plus size={16} weight="bold" /> New Activity
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            <div className="md:col-span-2 xl:col-span-2 relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input data-testid="activities-search"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="Search company, contact, email, phone..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm" />
            </div>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger data-testid="activities-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All statuses</SelectItem>
                <SelectItem value="Open">Open</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v })}>
              <SelectTrigger data-testid="activities-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All types</SelectItem>
                {ACTIVITY_TYPE_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.date} onValueChange={(v) => setFilters({ ...filters, date: v })}>
              <SelectTrigger data-testid="activities-date-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All dates</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="tomorrow">Tomorrow</SelectItem>
                <SelectItem value="next7">Next 7 days</SelectItem>
                <SelectItem value="next30">Next 30 days</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.priority} onValueChange={(v) => setFilters({ ...filters, priority: v })}>
              <SelectTrigger data-testid="activities-priority-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All priorities</SelectItem>
                <SelectItem value="A">Priority A</SelectItem>
                <SelectItem value="B">Priority B</SelectItem>
                <SelectItem value="C">Priority C</SelectItem>
              </SelectContent>
            </Select>
            {user?.role === "admin" && (
              <Select value={filters.agent_id} onValueChange={(v) => setFilters({ ...filters, agent_id: v })}>
                <SelectTrigger data-testid="activities-agent-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All agents</SelectItem>
                  <SelectItem value="_unassigned">Unassigned</SelectItem>
                  {agentOptions.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      )}

      {isAdmin && (
        <BulkActionBar count={selected.size} onClear={() => setSelected(new Set())}>
          <button onClick={bulkComplete} data-testid="bulk-complete"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#10B981] hover:bg-[#0EA371] text-white text-xs font-semibold">
            <CheckCircle size={14} weight="bold" /> Complete
          </button>
          <button onClick={exportSelected} data-testid="bulk-export-activities"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-white text-xs font-semibold">
            <DownloadSimple size={14} weight="bold" /> Export Selected
          </button>
          <button onClick={() => setConfirmDelete(true)} data-testid="bulk-delete-activities"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white text-xs font-semibold">
            <Trash size={14} weight="bold" /> Delete
          </button>
        </BulkActionBar>
      )}

      {/* Desktop table */}
      <div className="hidden md:block bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full min-w-[1100px]" data-testid="activities-table">
          <thead className="bg-gray-50">
            <tr>
              {isAdmin && (
                <th className="py-3 px-4 w-10">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll}
                    data-testid="activities-select-all"
                    className="w-4 h-4 rounded border-gray-300 cursor-pointer" />
                </th>
              )}
              <th className="text-left text-[11px] uppercase tracking-wider text-gray-500 font-semibold py-3 px-4 w-10"></th>
              <th className="text-left text-[11px] uppercase tracking-wider text-gray-500 font-semibold py-3 px-4">
                <SortHeader label="Type" k="activity_type" />
              </th>
              <th className="text-left text-[11px] uppercase tracking-wider text-gray-500 font-semibold py-3 px-4">
                <SortHeader label="Activity" k="title" />
              </th>
              <th className="text-left text-[11px] uppercase tracking-wider text-gray-500 font-semibold py-3 px-4">
                <SortHeader label="Lead" k="lead_company" />
              </th>
              <th className="text-left text-[11px] uppercase tracking-wider text-gray-500 font-semibold py-3 px-4">
                <SortHeader label="Follow-up date" k="next_follow_up_date" />
              </th>
              <th className="text-left text-[11px] uppercase tracking-wider text-gray-500 font-semibold py-3 px-4">
                <SortHeader label="Updated" k="updated_at" />
              </th>
              {user?.role === "admin" && (
                <th className="text-left text-[11px] uppercase tracking-wider text-gray-500 font-semibold py-3 px-4">
                  <SortHeader label="Agent" k="agent_name" />
                </th>
              )}
              <th className="text-left text-[11px] uppercase tracking-wider text-gray-500 font-semibold py-3 px-4">
                <SortHeader label="Priority" k="priority" />
              </th>
              <th className="text-left text-[11px] uppercase tracking-wider text-gray-500 font-semibold py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-10 text-gray-500">Loading activities...</td></tr>
            ) : sortedActivities.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-10 text-gray-500">No activities found.</td></tr>
            ) : sortedActivities.map((a) => {
              const lead = leadsMap[a.lead_id];
              const overdue = isOverdue(a);
              const iconName = ACTIVITY_ICONS[a.activity_type] || "Circle";
              const Icon = PhosphorIcons[iconName] || PhosphorIcons.Circle;
              const color = ACTIVITY_COLORS[a.activity_type] || "#6B7280";
              const completed = a.status === "Completed";
              return (
                <tr key={a.id} className={`border-t border-gray-100 hover:bg-gray-50 ${overdue ? "bg-red-50/40" : ""} ${selected.has(a.id) ? "bg-yellow-50/50" : ""}`}
                  data-testid={`activity-row-${a.id}`}>
                  {isAdmin && (
                    <td className="py-3 px-4">
                      <input type="checkbox" checked={selected.has(a.id)}
                        onChange={() => toggleOne(a.id)}
                        data-testid={`activity-select-${a.id}`}
                        className="w-4 h-4 rounded border-gray-300 cursor-pointer" />
                    </td>
                  )}
                  <td className="py-3 px-4 w-10">
                    <button onClick={() => toggleStatus(a)} data-testid={`status-circle-${a.id}`}
                      title={completed ? "Reopen" : "Mark completed"}
                      className="hover:scale-110 transition-transform">
                      {completed
                        ? <CheckCircle size={22} weight="fill" className="text-[#10B981]" />
                        : <Circle size={22} weight="regular" className="text-gray-300 hover:text-[#10B981]" />}
                    </button>
                  </td>
                  <td className="py-3 px-4">
                    <div className="inline-flex items-center gap-2 px-2 py-1 rounded-md text-[11px] font-bold"
                      style={{ background: `${color}12`, color, border: `1px solid ${color}33` }}>
                      <Icon size={13} weight="bold" />
                      {a.activity_type}
                    </div>
                  </td>
                  <td className="py-3 px-4 cursor-pointer" onClick={() => openEdit(a)}>
                    <div className={`text-sm font-medium ${completed ? "text-gray-500 line-through" : "text-gray-900"} hover:text-[#1A3A21]`}>
                      {activityTitle(a, lead)}
                    </div>
                    {a.description && (
                      <div className="text-[11px] text-gray-500 truncate max-w-[360px]">{a.description}</div>
                    )}
                  </td>
                  <td className="py-3 px-4 text-sm">
                    {lead ? (
                      <Link to={`/leads/${a.lead_id}`} className="text-[#1A3A21] hover:underline font-medium inline-flex items-center gap-1">
                        {lead.company_name} <ArrowSquareOut size={12} />
                      </Link>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className={`py-3 px-4 text-sm ${overdue ? "text-red-600 font-semibold" : "text-gray-700"}`}>
                    {a.next_follow_up_date ? fmtDateTime(a.next_follow_up_date)
                      : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-500" data-testid={`activity-updated-${a.id}`}>
                    {a.updated_at
                      ? fmtDateTime(a.updated_at)
                      : a.created_at
                        ? fmtDateTime(a.created_at)
                        : <span className="text-gray-400">—</span>}
                  </td>
                  {user?.role === "admin" && (
                    <td className="py-3 px-4 text-sm text-gray-700">
                      {agentsMap[a.agent_id]?.name || "—"}
                    </td>
                  )}
                  <td className="py-3 px-4"><PriorityBadge priority={a.priority || "B"} /></td>
                  <td className="py-3 px-4">
                    <button onClick={() => deleteAct(a)} data-testid={`activity-delete-${a.id}`}
                      className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600">
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2" data-testid="activities-cards">
        {loading ? <div className="text-center text-gray-500 py-10">Loading...</div>
          : sortedActivities.length === 0 ? <div className="text-center text-gray-500 py-10">No activities.</div>
          : sortedActivities.map((a) => {
            const lead = leadsMap[a.lead_id];
            const overdue = isOverdue(a);
            const completed = a.status === "Completed";
            const color = ACTIVITY_COLORS[a.activity_type] || "#6B7280";
            return (
              <div key={a.id} className={`bg-white border rounded-lg p-3 flex gap-3 ${overdue ? "border-red-200" : "border-gray-200"}`}>
                <button onClick={() => toggleStatus(a)} className="flex-shrink-0 mt-0.5">
                  {completed
                    ? <CheckCircle size={22} weight="fill" className="text-[#10B981]" />
                    : <Circle size={22} weight="regular" className="text-gray-300" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold uppercase" style={{ color }}>{a.activity_type}</span>
                    <PriorityBadge priority={a.priority || "B"} />
                  </div>
                  <div onClick={() => openEdit(a)} className={`text-sm font-medium ${completed ? "line-through text-gray-500" : "text-gray-900"}`}>
                    {activityTitle(a, lead)}
                  </div>
                  {lead && (
                    <Link to={`/leads/${a.lead_id}`} className="text-[11px] text-[#1A3A21] hover:underline">
                      {lead.company_name}
                    </Link>
                  )}
                  {a.next_follow_up_date && (
                    <div className={`text-[11px] ${overdue ? "text-red-600 font-semibold" : "text-gray-500"}`}>
                      {fmtDateTime(a.next_follow_up_date)}
                    </div>
                  )}
                </div>
                <button onClick={() => deleteAct(a)} className="text-gray-300 hover:text-red-600 self-start">
                  <Trash size={14} />
                </button>
              </div>
            );
          })}
      </div>

      <AddActivityModal
        open={modalOpen}
        onOpenChange={(o) => { setModalOpen(o); if (!o) { setEditing(null); setCreateMode(false); } }}
        leadId={createMode ? undefined : editing?.lead_id}
        editing={editing}
        onCreated={load}
        onDeleted={load}
      />

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete activities"
        destructive
        confirmLabel="Delete"
        message={`You are about to permanently delete ${selected.size} ${selected.size === 1 ? "activity" : "activities"}. They will be removed from all dashboard and report counts. This action cannot be undone. Continue?`}
        onConfirm={bulkDelete}
      />
    </div>
  );
}
